import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import NoData from '@rio-cloud/rio-uikit/NoData';

import BrakeLiningBarChart from '@/features/brakes/BrakeLiningBarChart';
import BrakeLiningFilters from '@/features/brakes/BrakeLiningFilters';
import { useBrakesRequestData } from '@/features/brakes/hooks/useBrakesRequestData';
import CommonRightToolBar from '@/features/common/components/CommonRightToolBar';
import { TABLE_BRAKE_LINING } from '@/features/common/components/dialogs/dialogConstants';
import {
    DEFAULT_COLUMNS_CONFIG,
    MaintenanceTable,
    renderNullable,
} from '@/features/common/components/maintenance-table/MaintenanceTable';
import { ColumnConfig, RowStatusMapper } from '@/features/common/components/maintenance-table/types';
import NoDataSupplied from '@/features/common/components/NoDataSupplied';
import TimestampAndMileage from '@/features/common/components/TimestampAndMileage';
import { useUserSettings } from '@/features/common/hooks/useUserSettings';
import { axePositionToAxleNumber } from '@/features/utils';
import {
    BrakeLiningColumns,
    BrakeLiningSettings,
    BrakeSideData,
    GroupedBrake,
    Status,
} from '@/generated/GeneratedApiTypes';
import { useDownloadCsvFileMutation } from '@/services/vehicleDiagnosticsApi';

import '@/features/common/components/maintenance-table/style.css';

type AxleRenderProps = {
    data: GroupedBrake;
};

const AxleRenderer = ({ data }: AxleRenderProps) => {
    const axleNumber = axePositionToAxleNumber(data.axle_position);
    return renderNullable(axleNumber, 'intl-msg:vehicle-diagnostics.maintenance.table.noDataSupplied');
};

const renderAxleSide = (sideData?: BrakeSideData) => {
    if (sideData === undefined) {
        return <NoData text={<FormattedMessage id="intl-msg:vehicle-diagnostics.maintenance.table.noData" />} />;
    }
    const { value, status, timestamp, mileage } = sideData;
    return (
        <>
            <BrakeLiningBarChart value={value} status={status} />
            <TimestampAndMileage timestamp={timestamp} mileage={mileage} />
        </>
    );
};

const BRAKES_COLUMNS_CONFIG: ColumnConfig[] = [
    ...DEFAULT_COLUMNS_CONFIG,
    {
        name: 'left',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.brakes.left',
        sortingValue: 'left',
        hidden: false,
        sortable: true,
    },
    {
        name: 'axle',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.brakes.axle',
        sortingValue: 'axlePosition',
        hidden: false,
        sortable: true,
    },
    {
        name: 'right',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.brakes.right',
        sortingValue: 'right',
        hidden: false,
        sortable: true,
    },
];

const statusMapper: RowStatusMapper = (row: GroupedBrake) => {
    const statuses = new Set([row.left?.status, row.right?.status]);
    if (statuses.has(Status.DANGER)) {
        return 'danger';
    }
    if (statuses.has(Status.WARNING)) {
        return 'warning';
    }
    return '';
};

const rowDetailRenderer = (data: GroupedBrake) => {
    const { left, right } = data;
    if (!left && !right) {
        return (
            <td colSpan={3} className="text-center">
                <NoDataSupplied />
            </td>
        );
    }
    return (
        <>
            <td className="bar-chart" data-field="left">
                {renderAxleSide(data.left)}
            </td>
            <td data-field="axlePosition">
                <AxleRenderer data={data} />
            </td>
            <td className="bar-chart" data-field="right">
                {renderAxleSide(data.right)}
            </td>
        </>
    );
};

export const BrakeLiningTable = () => {
    const { settings: userSettings } = useUserSettings<BrakeLiningSettings>(TABLE_BRAKE_LINING);
    const {
        pagination,
        sorting,
        data,
        isLoading,
        isFetching,
        isError,
        max,
        min,
        setRange,
        error,
        assetIds,
        accountIds,
        workshopIds,
        criticalityStatus,
        setCriticalityStatus,
    } = useBrakesRequestData({});
    const [downloadFile, { isLoading: isDownloading }] = useDownloadCsvFileMutation();

    const handleDownload = useCallback(() => {
        downloadFile({
            min,
            max,
            pageSize: pagination.pageSize,
            pageNumber: pagination.pageNumber,
            sortBy: sorting.sortBy,
            sortDirection: sorting.sortDirection,
            assetIds,
            accountIds,
            workshopIds,
            status: criticalityStatus,
        });
    }, [
        downloadFile,
        max,
        min,
        pagination.pageNumber,
        pagination.pageSize,
        accountIds,
        assetIds,
        workshopIds,
        sorting.sortBy,
        sorting.sortDirection,
        criticalityStatus,
    ]);

    const visibleColumns = useMemo(() => {
        const columns = userSettings?.columns;
        return BRAKES_COLUMNS_CONFIG.filter((col) => {
            if (columns && col.name in columns) {
                return columns[col.name as keyof BrakeLiningColumns] ?? false;
            }
            return !col.hidden;
        }).map((col) => ({ ...col, hidden: false }));
    }, [userSettings]);

    return (
        <MaintenanceTable
            columnConfig={visibleColumns}
            queryState={{ isError, error: error as FetchBaseQueryError, isFetching, isLoading }}
            pageMetadata={data?.page}
            pagination={pagination}
            sorting={sorting}
            rows={data?.content}
            leftToolbar={
                <BrakeLiningFilters
                    minValue={min}
                    maxValue={max}
                    onRangeChange={setRange}
                    criticalityStatus={criticalityStatus}
                    onCriticalityChange={setCriticalityStatus}
                    isLoading={isLoading}
                />
            }
            rightToolbar={
                <CommonRightToolBar
                    onExportClick={handleDownload}
                    isDownloading={isDownloading}
                    settingsDialog={TABLE_BRAKE_LINING}
                />
            }
            rowStatusMapper={statusMapper}
            rowDetailRenderer={rowDetailRenderer}
            detailViewTabParam="VIEW_DETAIL_WEAR_AND_TEAR"
        />
    );
};
